import { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CONTENT_PUBLIC_URL, validateStoriesFile } from '@dnyaneshwari/shared';
import type { MauliQuestion, MauliStory, StoriesFile } from '@dnyaneshwari/shared';
import { getPublishedStories, publishStories } from '@/services/adminService';

const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;
const LETTERS = ['अ', 'ब', 'क', 'ड'];

/**
 * "माऊलींविषयी": the stories of Sant Dnyaneshwar and their quiz questions,
 * for review and editing. Loads the published document (or, before the
 * first publish, the bundled stories.json), lets you change any question,
 * and publishes the whole set as one Firestore document that every app
 * reads on launch. The app keeps the bundled file as its offline fallback,
 * so "Download JSON" is for updating that file in the repo.
 */
export default function StoriesPage() {
  const [file, setFile] = useState<StoriesFile | null>(null);
  const [source, setSource] = useState<'published' | 'bundled' | ''>('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const published = await getPublishedStories().catch(() => null);
      if (published) { setFile(published); setSource('published'); return; }
      const r = await fetch(`${CONTENT_BASE}/stories.json`);
      if (r.ok) { setFile(await r.json()); setSource('bundled'); }
      else setStatus('Could not load stories.json');
    })();
  }, []);

  const problems = useMemo(() => (file ? validateStoriesFile(file) : []), [file]);
  const totalQ = file?.stories.reduce((n, s) => n + s.questions.length, 0) ?? 0;

  function update(fn: (f: StoriesFile) => void) {
    setFile((f) => { if (!f) return f; const copy = structuredClone(f); fn(copy); return copy; });
    setDirty(true);
  }
  const editStory = (id: string, fn: (s: MauliStory) => void) => update((f) => { const s = f.stories.find((x) => x.id === id); if (s) fn(s); });
  const editQ = (sid: string, qid: string, fn: (q: MauliQuestion) => void) => editStory(sid, (s) => { const q = s.questions.find((x) => x.id === qid); if (q) fn(q); });

  function addQuestion(s: MauliStory) {
    let id = `${s.id}-q${s.questions.length + 1}`;
    while (s.questions.some((q) => q.id === id)) id += 'x';
    editStory(s.id, (st) => st.questions.push({ id, q: '', options: ['', '', '', ''], answer: 0, explanation: '' }));
    setOpen((o) => ({ ...o, [s.id]: true }));
  }

  function addStory() {
    const id = prompt('Story id (English letters, e.g. haripath):')?.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!id || !file) return;
    if (file.stories.some((s) => s.id === id)) { setStatus(`A story with id "${id}" already exists.`); return; }
    update((f) => f.stories.push({ id, order: f.stories.length + 1, title: '', era: '', summary: '', paragraphs: [''], questions: [] }));
    setOpen((o) => ({ ...o, [id]: true }));
  }

  async function publish() {
    if (!file || problems.length) return;
    setBusy(true);
    try {
      const v = await publishStories(file);
      setFile((f) => (f ? { ...f, version: v } : f));
      setDirty(false);
      setSource('published');
      setStatus(`Published version ${v}. Apps pick it up on their next launch.`);
    } catch (e) {
      setStatus(`Publish failed: ${(e as Error).message}`);
    }
    setBusy(false);
  }

  function download() {
    if (!file) return;
    const { publishedAt: _a, publishedBy: _b, ...clean } = file as StoriesFile & { publishedAt?: unknown; publishedBy?: unknown };
    const blob = new Blob([JSON.stringify(clean, null, 2) + '\n'], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'stories.json';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (!file) return <div className="p-8 text-gray-500">{status || 'Loading…'}</div>;

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">माऊलींविषयी · कथा आणि प्रश्नमंजुषा</h1>
          <p className="text-sm text-gray-500 mt-1">
            {file.stories.length} stories · {totalQ} questions · version {file.version} · loaded from {source === 'published' ? 'the published document' : 'the bundled file (never published yet)'}
          </p>
          <p className="text-sm text-gray-500">Correct answers are shown in green. Change anything, then Publish: every app reads the new set on its next launch, no app update needed.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addStory} className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-1"><PlusIcon className="w-4 h-4" /> Story</button>
          <button onClick={download} className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Download JSON</button>
          <button onClick={publish} disabled={busy || !dirty || problems.length > 0} className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white font-medium disabled:opacity-40 hover:bg-amber-700">
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <b>Fix before publishing:</b>
          <ul className="list-disc pl-5 mt-1">{problems.slice(0, 8).map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}
      {status && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{status}</div>}

      <label className="block text-xs text-gray-500 mb-4">Source note (shown under the story list in the app)
        <input value={file.sourceNote} onChange={(e) => update((f) => { f.sourceNote = e.target.value; })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
      </label>

      <div className="space-y-3">
        {[...file.stories].sort((a, b) => a.order - b.order).map((s) => {
          const isOpen = !!open[s.id];
          return (
            <div key={s.id} className="bg-white border border-gray-200 rounded-xl">
              <button onClick={() => setOpen((o) => ({ ...o, [s.id]: !isOpen }))} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                {isOpen ? <ChevronDownIcon className="w-5 h-5 text-gray-400" /> : <ChevronRightIcon className="w-5 h-5 text-gray-400" />}
                <span className="w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">{s.order}</span>
                <span className="flex-1">
                  <span className="font-semibold text-gray-900">{s.title || <i className="text-gray-400">untitled</i>}</span>
                  <span className="text-sm text-gray-500 ml-2">{s.era}</span>
                </span>
                <span className="text-sm text-gray-500">{s.questions.length} questions</span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-gray-100 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                    <Field label="Title" value={s.title} onChange={(v) => editStory(s.id, (st) => { st.title = v; })} />
                    <Field label="Era / place" value={s.era} onChange={(v) => editStory(s.id, (st) => { st.era = v; })} />
                    <Field label="Order" value={String(s.order)} onChange={(v) => editStory(s.id, (st) => { st.order = Number(v) || st.order; })} />
                  </div>
                  <Field label="One-line summary" value={s.summary} onChange={(v) => editStory(s.id, (st) => { st.summary = v; })} />
                  <label className="block text-xs text-gray-500">Story (leave a blank line between paragraphs)
                    <textarea value={s.paragraphs.join('\n\n')} rows={8} onChange={(e) => editStory(s.id, (st) => { st.paragraphs = e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean); })} className="mt-1 w-full border rounded-lg px-3 py-2 text-[15px] leading-relaxed" />
                  </label>

                  <div className="space-y-3">
                    {s.questions.map((q, qi) => (
                      <div key={q.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-bold text-gray-400 mt-2 w-6">{qi + 1}.</span>
                          <input value={q.q} placeholder="Question" onChange={(e) => editQ(s.id, q.id, (x) => { x.q = e.target.value; })} className="flex-1 border rounded-lg px-3 py-2 text-[15px] font-semibold" />
                          <button onClick={() => editStory(s.id, (st) => { st.questions = st.questions.filter((x) => x.id !== q.id); })} className="p-2 text-gray-400 hover:text-red-600" title="Delete question"><TrashIcon className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 pl-8">
                          {q.options.map((opt, oi) => {
                            const correct = oi === q.answer;
                            return (
                              <label key={oi} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${correct ? 'bg-green-50 border-green-400' : 'bg-white border-gray-200'}`}>
                                <input type="radio" name={`ans-${q.id}`} checked={correct} onChange={() => editQ(s.id, q.id, (x) => { x.answer = oi; })} title="Mark as the correct answer" />
                                <span className={`text-xs font-bold ${correct ? 'text-green-700' : 'text-gray-400'}`}>{LETTERS[oi]}</span>
                                <input value={opt} onChange={(e) => editQ(s.id, q.id, (x) => { x.options[oi] = e.target.value; })} className="flex-1 bg-transparent text-[14px] outline-none" />
                                {correct && <CheckCircleIcon className="w-4 h-4 text-green-600" />}
                              </label>
                            );
                          })}
                        </div>
                        <input value={q.explanation} placeholder="Explanation shown after answering" onChange={(e) => editQ(s.id, q.id, (x) => { x.explanation = e.target.value; })} className="mt-2 ml-8 w-[calc(100%-2rem)] border rounded-lg px-3 py-1.5 text-[13px] text-gray-700" />
                      </div>
                    ))}
                    <button onClick={() => addQuestion(s)} className="text-sm text-amber-700 font-medium flex items-center gap-1 pl-8"><PlusIcon className="w-4 h-4" /> Add question</button>
                  </div>
                  <button onClick={() => { if (confirm(`Delete the story "${s.title}" and its questions?`)) update((f) => { f.stories = f.stories.filter((x) => x.id !== s.id); }); }} className="text-sm text-red-600 flex items-center gap-1"><TrashIcon className="w-4 h-4" /> Delete story</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-xs text-gray-500">{label}
      <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
    </label>
  );
}
